import { flushSync } from "react-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { remainingMoveMs, SETTLE_MARGIN_MS } from "./motionTiming";

type RevealView = { open: boolean; moving: boolean; move: number };

/**
 * Drives a section's height motion. The frame holds the header and the body. At rest its height is
 * auto. A move starts when `open` changes after the first render, or when the measured content
 * (open) or header (closed) height changes. It writes the old pixel height, then the new one, so
 * the CSS transition runs. A content change noticed mid-move is not followed: when the move ends
 * the target is measured again, and if it differs a fresh move starts from where the last one ended.
 * Only an `open` flip (the editor reversing) retargets a running move at once. A move ends by a
 * timer from the remaining transition time plus a margin, or at once when nothing is running
 * (reduced motion). `rendered` is true while open and while closing, so the body stays mounted
 * until the frame has contracted; the commit that unmounts it releases the frame's height. A content change React causes is caught in the same commit, before
 * paint; the resize observer covers size changes React does not cause.
 */
export function useBodyReveal(open: boolean) {
  const frameRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const restHeight = useRef(0);
  const wasMoving = useRef(false);
  const [view, setView] = useState<RevealView>({ open, moving: false, move: 0 });

  if (view.open !== open) setView({ open, moving: true, move: view.move + 1 });

  const { moving, move } = view;

  useLayoutEffect(() => {
    const target = measureTarget(open, contentRef.current, headerRef.current);
    const frame = frameRef.current;
    const startedFromRest = !wasMoving.current;
    wasMoving.current = moving;
    if (!moving) {
      restHeight.current = target;
      if (frame) frame.style.height = "";
      return;
    }
    if (frame && startedFromRest) {
      frame.style.height = `${restHeight.current}px`;
      void frame.offsetHeight;
    }
    if (frame) frame.style.height = `${target}px`;
    restHeight.current = target;

    const finish = (insideCommit = false) => {
      const settledFrame = frameRef.current;
      const latest = measureTarget(open, contentRef.current, headerRef.current);
      if (settledFrame && latest !== restHeight.current) {
        settledFrame.style.height = "";
        settledFrame.style.height = `${restHeight.current}px`;
        void settledFrame.offsetHeight;
        setView(current => (current.move === move ? { ...current, move: move + 1 } : current));
        return;
      }
      const endMove = () =>
        setView(current => (current.move === move ? { ...current, moving: false } : current));
      if (open) {
        if (settledFrame) settledFrame.style.height = "";
        endMove();
        return;
      }
      if (insideCommit) endMove();
      else flushSync(endMove);
    };
    const remaining = frame ? remainingMoveMs(frame) : 0;
    if (remaining <= 0) {
      finish(true);
      return;
    }
    const timer = window.setTimeout(() => finish(), remaining + SETTLE_MARGIN_MS);
    return () => window.clearTimeout(timer);
  }, [moving, move, open]);

  useLayoutEffect(() => {
    if (moving) return;
    const target = measureTarget(open, contentRef.current, headerRef.current);
    if (target === restHeight.current) return;
    const frame = frameRef.current;
    if (frame) {
      frame.style.height = `${restHeight.current}px`;
      void frame.offsetHeight;
    }
    setView(current => ({ ...current, moving: true, move: current.move + 1 }));
  });

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (wasMoving.current) return;
      const target = measureTarget(open, contentRef.current, headerRef.current);
      if (target === restHeight.current) return;
      const frame = frameRef.current;
      if (frame) frame.style.height = `${restHeight.current}px`;
      flushSync(() => setView(current => ({ ...current, moving: true, move: current.move + 1 })));
    });
    if (contentRef.current) observer.observe(contentRef.current);
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [open]);

  return { rendered: open || moving, moving, frameRef, contentRef, headerRef };
}

function measureTarget(open: boolean, content: HTMLElement | null, header: HTMLElement | null) {
  const element = open ? content : header;
  return element ? element.getBoundingClientRect().height : 0;
}
